
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { TreatmentServiceCard } from "@/components/TreatmentServiceCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PageTitle } from "@/components/PageTitle";

const CosmeticDentistry = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQs specific to Cosmetic Dentistry
  const faqs = [
    {
      question: "What is cosmetic dentistry?",
      answer: "Cosmetic dentistry focuses on improving the appearance of your teeth, smile, and overall facial aesthetics. It includes procedures like teeth whitening, veneers, bonding, implants, and smile makeovers designed to enhance your smile's appearance."
    },
    {
      question: "How long do porcelain veneers last?",
      answer: "With proper care and maintenance, porcelain veneers typically last between 10-15 years. Regular dental check-ups, good oral hygiene, and avoiding habits like nail-biting or using teeth as tools will help extend their lifespan."
    },
    {
      question: "Is teeth whitening safe?",
      answer: "Professional teeth whitening conducted by a dentist is safe for most patients. We use high-quality products and personalized approaches to minimize sensitivity while achieving optimal results. We'll evaluate your dental health before recommending any whitening treatment."
    },
    {
      question: "Can cosmetic dentistry fix crooked teeth?",
      answer: "Yes, cosmetic dentistry offers several solutions for crooked teeth. Options include veneers, which can mask mild misalignments, and orthodontic treatments like Invisalign for more significant corrections. During your consultation, we'll recommend the most suitable approach for your specific needs."
    },
    {
      question: "How much does cosmetic dentistry cost?",
      answer: "The cost of cosmetic dentistry varies depending on the procedure, complexity, and materials used. Simple treatments like professional whitening are more affordable, while comprehensive smile makeovers involving multiple procedures will have a higher investment. We offer financing options and will provide detailed cost information during your consultation."
    }
  ];

  // Testimonials for Cosmetic Dentistry
  const testimonials = [
    {
      name: "Anjali Mehta",
      comment: "My smile transformation with veneers at Asrani Dental has been life-changing! Dr. Asrani understood exactly what I wanted and delivered perfect results. I can't stop smiling now and have received countless compliments on my new look.",
      rating: 5
    },
    {
      name: "Vikram Singhania",
      comment: "After years of being embarrassed about my discolored teeth, I finally decided to try professional whitening at Asrani Dental. The results exceeded my expectations - my teeth are now several shades whiter and look completely natural. The team was professional and made the process comfortable.",
      rating: 5
    },
    {
      name: "Pooja Sharma",
      comment: "Dr. Asrani completely transformed my smile with a combination of crowns and veneers. The attention to detail and the care taken to ensure everything looked natural was impressive. The entire team made me feel comfortable throughout the process.",
      rating: 5
    }
  ];

  const handleBookAppointment = () => {
    window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank");
  };

  return (
    <div className="min-h-screen">
      <PageTitle title="Teeth Whitening in Nagpur | Cost of Teeth Whitening in Nagpur" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Cosmetic Dentistry in Nagpur
            </h1>
            <p className="text-lg text-dental-muted">
              Transform your smile with our state-of-the-art cosmetic dental procedures designed to enhance your natural beauty.
            </p>
            <div className="mt-8">
              <Button className="btn-primary" onClick={handleBookAppointment}>
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionTitle 
                title="Enhance Your Smile" 
                subtitle="Cosmetic Dentistry"
                description="Our cosmetic dentistry services are designed to improve the appearance of your teeth and give you a confident, radiant smile that makes a lasting impression."
                alignment="left"
              />
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Natural-Looking Results</h3>
                    <p className="text-dental-muted">We focus on creating natural-looking enhancements that complement your facial features and personal style.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Custom Treatment Plans</h3>
                    <p className="text-dental-muted">Each smile makeover is uniquely designed to address your specific concerns and aesthetic goals.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Advanced Techniques</h3>
                    <p className="text-dental-muted">We use the latest materials and techniques to provide long-lasting, beautiful results with minimal discomfort.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Comprehensive Options</h3>
                    <p className="text-dental-muted">From simple teeth whitening to complete smile makeovers, we offer a full range of cosmetic solutions.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative">
                <div className="w-full h-full absolute -top-6 -right-6 rounded-2xl border-2 border-dental-accent/20"></div>
                <img 
                  src="/lovable-uploads/c2bbd156-50b4-494f-afa7-5c39b9e99eea.png" 
                  alt="Beautiful smile - Cosmetic Dentistry at Asrani Dental" 
                  className="w-full h-auto rounded-2xl shadow-soft object-cover" 
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Treatments Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle 
            title="Our Cosmetic Dentistry Treatments" 
            subtitle="Services We Offer"
            description="We provide a wide range of cosmetic dentistry services to help you achieve the smile of your dreams."
            alignment="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <TreatmentServiceCard 
              title="Teeth Whitening"
              description="Professional whitening treatments that deliver dramatic results, brightening your smile by several shades in just one visit."
              imageUrl="/lovable-uploads/545cfd03-f389-4ab9-ac50-295c248f3cd9.png"
            />
            
            <TreatmentServiceCard 
              title="Porcelain Veneers"
              description="Custom-made, ultra-thin porcelain shells bonded to the front of teeth to improve their appearance and correct various aesthetic issues."
              imageUrl="/lovable-uploads/79fb2d9d-3034-47e3-82ca-b9eb1d45702f.png"
            />
            
            <TreatmentServiceCard 
              title="Dental Bonding"
              description="A procedure where tooth-colored resin is applied to improve the appearance of chipped, cracked, or discolored teeth."
              imageUrl="/lovable-uploads/49df8a59-4334-4e73-bcb6-d9838e803286.png"
            />
            
            <TreatmentServiceCard 
              title="Smile Makeovers"
              description="Comprehensive treatment plans combining multiple procedures to completely transform your smile."
              imageUrl="/lovable-uploads/a584a4c0-a632-450f-8e90-7280ee743164.png"
            />
            
            <TreatmentServiceCard 
              title="Gum Contouring"
              description="Reshaping the gum line to create a more balanced, aesthetically pleasing smile and correct a 'gummy' appearance."
              imageUrl="/lovable-uploads/7b5dfe8a-e79d-4e59-b614-148e8a422f5a.png"
            />
            
            <TreatmentServiceCard 
              title="Tooth-Colored Fillings"
              description="Natural-looking composite restorations that blend seamlessly with your teeth while repairing decay or damage."
              imageUrl="/lovable-uploads/c406f99e-ae7c-4b89-b40f-800c4f2f76f6.png"
            />
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle 
            title="Patient Testimonials" 
            subtitle="What Our Patients Say"
            description="Hear from our patients about their experiences with our cosmetic dentistry services."
            alignment="center"
          />
          
          <div className="max-w-4xl mx-auto mt-12">
            <ServiceTestimonials testimonials={testimonials} />
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Common Questions"
            description="Find answers to common questions about cosmetic dentistry services."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>
      
      {/* Next Steps Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">Ready to Transform Your Smile?</h2>
            <p className="text-lg text-dental-muted mb-8">
              Contact our office today to schedule your cosmetic consultation. Let us help you achieve the beautiful, confident smile you've always wanted.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button className="btn-primary flex items-center text-base group" onClick={handleBookAppointment}>
                Book Appointment
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              
              <Button className="btn-secondary flex items-center text-base" onClick={() => window.location.href = '/contact'}>
                Contact Us
              </Button>
            </div>
            
            <div className="mt-10">
              <p className="text-dental-muted mb-4">Interested in our other dental services?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services/general-dentistry-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  General Dentistry
                </Link>
                <Link to="/services/orthodontics-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Orthodontics
                </Link>
                <Link to="/services/full-mouth-implants-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Full Mouth & Implants
                </Link>
                <Link to="/services/painless-rct-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Painless RCT
                </Link>
                <Link to="/services/pediatric-dentistry-in-nagpur" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Pediatric Dentistry
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CTASection
        title="Discover Your Best Smile"
        description="Our cosmetic dentistry services can help you achieve the smile you've always wanted. Schedule your consultation today."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Learn More"
        primaryButtonAction={handleBookAppointment}
        secondaryButtonAction={() => window.location.href = "/about"}
      />
      
      <Footer />
    </div>
  );
};

export default CosmeticDentistry;
